import axios from '@/axios.js'

// 이미지 텍스트화 캡처(여러 장)를 백그라운드에서 처리한다.
// 페이지 이동과 무관하게 끝까지 실행되어야 하므로 컴포넌트가 아니라 스토어 액션으로 둔다.
//
// source: 'exam'(기본, /exam/admin-question-edit-ai/v2) | 'question_pool'(/admin/question-pool/edit)
// 두 페이지가 질문 텍스트를 저장하는 API가 서로 달라서 source로 분기한다.
// choiceId를 넘기면(question_pool 한정) 필드가 아니라 해당 선지의 choice_text를 교체한다.
async function runOcrCaptureJob({ commit }, payload) {
  const {
    examSubjectId, examType, questionId, questionIndex, questionNumber, field, fieldLabel, images,
    source = 'exam', choiceId = null, sourcePath = null,
  } = payload
  const jobId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

  commit('ADD_JOB', {
    id: jobId,
    examSubjectId,
    examType,
    questionId,
    questionIndex,
    questionNumber,
    field,
    fieldLabel,
    source,
    choiceId,
    sourcePath,
    status: 'processing',
    createdAt: Date.now(),
    resultText: '',
    error: '',
    appliedLocally: false,
  })

  try {
    const results = await Promise.all(
      images.map((dataUrl) =>
        ocrCropImage(dataUrl).catch((e) => {
          console.error('이미지 텍스트화 실패:', e)
          return null
        })
      )
    )

    const joined = results.filter((t) => t !== null && t !== '').join('<br>')
    const failedCount = results.filter((t) => t === null).length

    if (!joined) {
      commit('UPDATE_JOB', { id: jobId, patch: { status: 'error', error: '텍스트화 결과가 없습니다.' } })
      return
    }

    await persistOcrResult({ source, questionId, field, choiceId, text: joined })

    commit('UPDATE_JOB', { id: jobId, patch: { status: 'done', resultText: joined, failedCount } })
  } catch (e) {
    console.error('텍스트화 작업 실패:', e)
    commit('UPDATE_JOB', { id: jobId, patch: { status: 'error', error: '텍스트화 작업 중 오류가 발생했습니다.' } })
  }
}

async function persistOcrResult({ source, questionId, field, choiceId, text }) {
  if (source === 'question_pool') {
    if (choiceId) {
      await axios.patch(`/api/v2/admin/question-pool/question/${questionId}/choice/${choiceId}`, { choice_text: text })
    } else {
      await axios.patch(`/api/v2/admin/question-pool/question/${questionId}`, { [field]: text })
    }
    return
  }

  if (choiceId) {
    await axios.put('/api/v2/exam/question/image_to_text', {
      question_id: questionId,
      question_multiple_list: [{ id: choiceId, choice_text: text }],
    })
    return
  }

  await axios.put('/api/v2/exam/question/image_to_text', {
    question_id: questionId,
    [field]: text,
  })
}

async function ocrCropImage(dataUrl) {
  const blob = await (await fetch(dataUrl)).blob()
  const formData = new FormData()
  formData.append('image', blob, 'crop.png')
  formData.append('engine', 'codex')

  const res = await axios.post('/api/v3/ai_system/question_ingestor/crop_ocr_test', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })

  if (res.data.result === 'success') {
    return res.data.data.text
  }
  throw new Error(res.data.detail || '텍스트화에 실패했습니다.')
}

const SOLVE_POLL_INTERVAL_MS = 4000
const SOLVE_POLL_MAX_ATTEMPTS = 90 // 약 6분

const SOLVE_KICKOFF_DETAIL_MESSAGE = {
  'Question is not texted.': '아직 텍스트화 되지 않은 문제입니다.',
  'Question has already been applied.': '이미 운영서버에 적용된 문제입니다.',
  'No answer file found.': '업로드된 해설 이미지를 찾을 수 없습니다.',
}

// 해설 생성(AI 재생성 / 원본 해설 생성)도 백그라운드로 처리한다.
// 이 엔드포인트들은 서버가 스레드로 던지고 바로 응답하는 구조라(완료를 기다리지 않음),
// 완료 여부는 solve_text가 다시 채워지는지 polling으로 확인한다.
async function runSolveRegenerateJob({ commit }, payload) {
  const { examSubjectId, examType, questionId, questionIndex, questionNumber, mode } = payload
  const isOriginal = mode === 'original'
  const jobId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

  commit('ADD_JOB', {
    id: jobId,
    examSubjectId,
    examType,
    questionId,
    questionIndex,
    questionNumber,
    field: 'solve_text',
    fieldLabel: isOriginal ? '원본 해설' : 'AI 해설',
    actionLabel: '생성',
    status: 'processing',
    createdAt: Date.now(),
    resultText: '',
    error: '',
    appliedLocally: false,
  })

  try {
    const endpoint = isOriginal
      ? '/api/v2/exam/question/original_solve_text_generate'
      : '/api/v2/exam/question/solve_text_regenerate'
    const kickoff = await axios.put(endpoint, { question_id: questionId })
    if (kickoff.data.result !== 'success') {
      throw kickoff.data.detail || '해설 생성 시작에 실패했습니다.'
    }
  } catch (e) {
    const detail = (e && e.response && e.response.data && e.response.data.detail) || (typeof e === 'string' ? e : null)
    const message = (detail && SOLVE_KICKOFF_DETAIL_MESSAGE[detail]) || '해설 생성 시작에 실패했습니다.'
    console.error('해설 생성 시작 실패:', e)
    commit('UPDATE_JOB', { id: jobId, patch: { status: 'error', error: message } })
    return
  }

  for (let attempt = 0; attempt < SOLVE_POLL_MAX_ATTEMPTS; attempt++) {
    await new Promise((resolve) => setTimeout(resolve, SOLVE_POLL_INTERVAL_MS))
    try {
      const res = await axios.get('/api/v4/exam/question?question_id=' + questionId)
      const solveText = res && res.data && res.data.data && res.data.data.solve_text
      if (solveText) {
        commit('UPDATE_JOB', { id: jobId, patch: { status: 'done', resultText: solveText } })
        return
      }
    } catch (e) {
      console.error('해설 생성 상태 확인 실패:', e)
    }
  }

  commit('UPDATE_JOB', {
    id: jobId,
    patch: { status: 'error', error: '생성 시간이 너무 오래 걸립니다. 잠시 후 새로고침해서 확인해주세요.' },
  })
}

export default {
  runOcrCaptureJob,
  runSolveRegenerateJob,
}
